/**
 * Week profile — the week-select detail beneath the deep Sankey.
 *
 * Same reading as the day chart, one level up: for the focused end-use, plots
 * every hour of the selected ISO week as ACTUAL metered load against the
 * EXPECTED curve, with the gap between them the observed after-hours potential
 * savings.
 * Unoccupied hours are shaded, so the weekly rhythm — five worked days and the
 * weekend — is visible as a shape rather than something to infer from numbers.
 *
 * The server builds this by concatenating the same day profiles the day chart
 * draws, so a week's $ is exactly the sum of its days' and the two views can
 * never disagree.
 *
 * Custom SVG (no charting dep), matching hourly-profile-chart.
 *
 * Copy contract: OBSERVED framing only — the figure is money already spent,
 * so no projected/forecast/will-reduce language.
 */
import type React from "react"

import { Box, CircularProgress, Stack, Typography } from "@mui/material"

import { useWeekProfile } from "../../data/use-week-profile"
import { Eyebrow } from "../../homepage/atoms"
import { enduseColor, enduseLabel } from "../variance-route/enduse-colors"
import { ChartDataTable } from "./chart-data-table"
import type { WeekPoint } from "./schedule-misalignment.types"
import { useFormatWasteCurrency } from "./use-format-waste-currency"

const MONO = "ui-monospace, 'SF Mono', Menlo, Consolas, monospace"
const VB_W = 720
const VB_H = 248
const PAD_L = 46
const PAD_R = 18
const PAD_T = 26
const PAD_B = 30
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

const Surface: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box
    sx={{
      mt: 2,
      borderRadius: 3,
      border: 1,
      borderColor: "grey.200",
      bgcolor: "grey.25",
      p: 2.5,
    }}
  >
    {children}
  </Box>
)

const LegendItem: React.FC<{
  children: React.ReactNode
  swatch: React.ReactNode
}> = ({ children, swatch }) => (
  <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
    {swatch}
    <Typography color="text.secondary" variant="body2">
      {children}
    </Typography>
  </Stack>
)

export const WeekProfileChart: React.FC<{
  siteId: string
  enduse: string
  week: string
}> = ({ siteId, enduse, week }) => {
  const formatCurrency = useFormatWasteCurrency()
  const { weekProfileData, weekProfileIsPending, weekProfileIsError } =
    useWeekProfile(siteId, enduse, week)
  const color = enduseColor(enduse)

  if (weekProfileIsPending) {
    return (
      <Surface>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <CircularProgress size={16} />
          <Typography color="text.secondary" variant="body2">
            Loading weekly detail…
          </Typography>
        </Stack>
      </Surface>
    )
  }

  const points: WeekPoint[] =
    weekProfileIsError || !weekProfileData?.available
      ? []
      : (weekProfileData?.points ?? [])

  if (points.length === 0) {
    return (
      <Surface>
        <Typography color="text.secondary" variant="body2">
          No weekly detail for this week.
        </Typography>
      </Surface>
    )
  }

  const days = weekProfileData?.days ?? []
  const plotW = VB_W - PAD_L - PAD_R
  const plotH = VB_H - PAD_T - PAD_B
  const maxRaw = Math.max(
    ...points.map((p) => Math.max(p.actual_kw, p.expected_kw)),
    1
  )
  const step = 10 ** Math.floor(Math.log10(maxRaw))
  const yMax = Math.ceil(maxRaw / step) * step
  const x = (i: number): number =>
    PAD_L +
    (points.length === 1 ? plotW / 2 : (i / (points.length - 1)) * plotW)
  const y = (kw: number): number => PAD_T + plotH - (kw / yMax) * plotH
  const path = (pick: (p: WeekPoint) => number): string =>
    points.map((p, i) => `${i ? "L" : "M"}${x(i)},${y(pick(p))}`).join(" ")

  // Unoccupied runs, shaded — the weekend shows up as one wide band.
  const bands: { from: number; to: number }[] = []
  points.forEach((p, i) => {
    if (p.occupied) {
      return
    }
    const last = bands.at(-1)
    if (last?.to === i - 1) {
      last.to = i
    } else {
      bands.push({ from: i, to: i })
    }
  })

  return (
    <Surface>
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: "baseline", flexWrap: "wrap", mb: 0.5 }}
      >
        <Typography sx={{ fontWeight: "bold" }} variant="body1">
          {enduseLabel(enduse)} — {weekProfileData?.label ?? week}
        </Typography>
        <Box sx={{ alignItems: "baseline", display: "flex", gap: 0.75 }}>
          <Typography
            sx={{ fontFamily: MONO, fontWeight: "bold" }}
            variant="body2"
          >
            {formatCurrency(weekProfileData?.waste_dollars_total ?? 0)}
          </Typography>
          <Eyebrow>observed this week</Eyebrow>
        </Box>
      </Stack>

      <Box
        component="svg"
        role="img"
        aria-label={`${enduseLabel(enduse)} actual versus expected load, ${
          weekProfileData?.label ?? week
        }`}
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        sx={{ width: "100%", height: "auto", display: "block" }}
      >
        {bands.map((b) => (
          <rect
            key={`band-${b.from}`}
            x={x(b.from)}
            y={PAD_T}
            width={Math.max(x(b.to) - x(b.from), 1)}
            height={plotH}
            fill="currentColor"
            opacity={0.04}
          />
        ))}

        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line
              x1={PAD_L}
              x2={PAD_L + plotW}
              y1={y(yMax * f)}
              y2={y(yMax * f)}
              stroke="currentColor"
              opacity={0.12}
            />
            <text
              x={PAD_L - 8}
              y={y(yMax * f) + 4}
              textAnchor="end"
              fontSize={10}
              fontFamily={MONO}
              fill="currentColor"
              opacity={0.55}
            >
              {Math.round(yMax * f)}
            </text>
          </g>
        ))}

        {/* one divider + label per day */}
        {days.map((d, di) => {
          const start = points.findIndex((p) => p.day === d)
          if (start < 0) {
            return null
          }
          let end = start
          while (end + 1 < points.length && points[end + 1].day === d) {
            end += 1
          }
          const mid = (x(start) + x(end)) / 2
          return (
            <g key={d}>
              {di > 0 ? (
                <line
                  x1={x(start)}
                  x2={x(start)}
                  y1={PAD_T}
                  y2={PAD_T + plotH}
                  stroke="currentColor"
                  opacity={0.18}
                />
              ) : null}
              <text
                x={mid}
                y={VB_H - 10}
                textAnchor="middle"
                fontSize={10}
                fill="currentColor"
                opacity={0.6}
              >
                {DOW[points[start].dow]}
              </text>
            </g>
          )
        })}

        <path
          d={path((p) => p.expected_kw)}
          fill="none"
          stroke="currentColor"
          strokeOpacity={0.45}
          strokeWidth={2}
          strokeDasharray="4 3"
        />
        <path
          d={path((p) => p.actual_kw)}
          fill="none"
          stroke={color}
          strokeWidth={2}
        />
      </Box>

      <ChartDataTable
        caption={`${enduseLabel(
          enduse
        )} actual against expected load, week ${week}, hour by hour`}
        columns={[
          "Hour",
          "Actual kW",
          "Expected kW",
          "Potential savings",
          "Schedule",
        ]}
        rows={points.map((p) => [
          `${DOW[p.dow]} ${String(p.hour).padStart(2, "0")}:00`,
          Math.round(p.actual_kw),
          Math.round(p.expected_kw),
          formatCurrency(p.waste_dollars),
          p.occupied ? "Open" : "Closed",
        ])}
      />

      <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", mt: 1 }}>
        <LegendItem
          swatch={
            <Box
              sx={{ width: 14, height: 2, bgcolor: color, borderRadius: 1 }}
            />
          }
        >
          Actual
        </LegendItem>
        <LegendItem
          swatch={
            <Box
              sx={{
                width: 14,
                height: 0,
                borderTop: "2px dashed",
                borderColor: "text.disabled",
              }}
            />
          }
        >
          Expected
        </LegendItem>
        <LegendItem
          swatch={
            <Box
              sx={{
                width: 14,
                height: 10,
                bgcolor: "text.primary",
                opacity: 0.08,
              }}
            />
          }
        >
          Outside operating hours
        </LegendItem>
      </Stack>
    </Surface>
  )
}
